import { useMemo, useState } from "react";

import { Link, Outlet } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import IconDiscover from "~icons/mingcute/angel-line";
import IconHome from "~icons/mingcute/home-1-line";
import IconUser from "~icons/mingcute/user-3-line";

import { Beian } from "@/features/shared/components/beian";
import { ThemeToggle } from "@/features/shared/components/theme-toggle";
import { Button } from "@/components/ui/button";
import { NavigationBarContext } from "@/hooks/navigation-bar-context";
import { useRouteOptions } from "@/hooks/use-route-options";
import { userQueryOptions } from "@/lib/client/user";
import { cn } from "@/lib/utils";
import { signOut } from "@/lib/client/auth";

interface IndexLayoutPageProps {
  roles: readonly string[];
}

type HeaderRouteOptions = {
  title?: string;
};

export function IndexLayoutPage({ roles }: IndexLayoutPageProps) {
  const routeOptions = useRouteOptions();
  const { data: user } = useQuery(userQueryOptions);
  const [hidden, setHidden] = useState(false);
  const navigationBarContextValue = useMemo(() => ({ hidden, setHidden }), [hidden]);

  return (
    <NavigationBarContext value={navigationBarContextValue}>
      <div>
        <div className="min-h-dvh">
          <div className="sticky top-0 mb-4 z-20 bg-background/80 backdrop-blur-sm border-b-border/50 border-b">
            <Header routeOptions={routeOptions} roles={roles} isLoggedIn={Boolean(user)} />
          </div>

          <div className="container max-w-3xl mx-auto px-4 md:px-6">
            <Outlet />
          </div>

          <Beian />
        </div>

        <NavigationBarContext.Consumer>
          {({ hidden }) => <Footer hidden={hidden} />}
        </NavigationBarContext.Consumer>
      </div>
    </NavigationBarContext>
  );
}

function Header({
  isLoggedIn,
  roles,
  routeOptions,
}: {
  isLoggedIn: boolean;
  roles: readonly string[];
  routeOptions: HeaderRouteOptions;
}) {
  return (
    <div className="container max-w-3xl mx-auto py-4 px-6 flex items-center justify-between">
      <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-foreground flex items-center flex-wrap gap-x-3 gap-y-1">
        {!routeOptions.title ? (
          <>
            <a href="/">
              <span className="text-red-500">CN</span> Apex Club
            </a>
            {roles.includes("admin") && (
              <Link
                data-umami-event="enter-admin"
                to="/admin"
                className="text-xs font-bold text-muted-foreground hover:text-primary transition-colors bg-muted/50 px-2 py-1 rounded-md uppercase tracking-wider"
              >
                管理端
              </Link>
            )}
          </>
        ) : (
          routeOptions.title
        )}
      </h1>

      <div className="flex items-center gap-2 md:gap-4">
        {isLoggedIn ? (
          <Button
            data-umami-event="logout"
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-primary font-medium"
            onClick={handleLogout}
          >
            退出登录
          </Button>
        ) : null}

        <ThemeToggle />
      </div>
    </div>
  );
}

function Footer({ hidden }: { hidden: boolean }) {
  return (
    <nav className={cn("sticky bottom-2 z-30 pointer-events-none", hidden && "hidden", "flex")}>
      <ul className="shadow-[0_-4px_20px_rgba(0,0,0,0.05)] dark:shadow-[0_-4px_20px_rgba(0,0,0,0.2)] border border-foreground/10 bg-background/80 backdrop-blur-lg h-auto w-auto mx-auto rounded-full flex flex-row p-1">
        <li>
          <Link
            data-umami-event="enter-home"
            to="/"
            activeProps={{
              className:
                "!text-primary bg-background/30 backdrop-blur-md shadow-sm border border-border/50",
            }}
            className="size-12 rounded-full text-foreground/50 flex items-center justify-center pointer-events-auto transition-all duration-200 hover:text-foreground/80 active:scale-95"
          >
            <IconHome className="size-5" />
          </Link>
        </li>

        <li>
          <Link
            data-umami-event="enter-history"
            to="/history"
            activeProps={{
              className:
                "!text-primary bg-background/30 backdrop-blur-md shadow-sm border border-border/50",
            }}
            className="size-12 rounded-full text-foreground/50 flex items-center justify-center pointer-events-auto transition-all duration-200 hover:text-foreground/80 active:scale-95"
          >
            <IconDiscover className="size-5" />
          </Link>
        </li>

        <li>
          <Link
            data-umami-event="enter-userspace"
            to="/userspace"
            activeProps={{
              className:
                "!text-primary bg-background/30 backdrop-blur-md shadow-sm border border-border/50",
            }}
            className="size-12 rounded-full text-foreground/50 flex items-center justify-center pointer-events-auto transition-all duration-200 hover:text-foreground/80 active:scale-95"
          >
            <IconUser className="size-5" />
          </Link>
        </li>
      </ul>
    </nav>
  );
}

function handleLogout() {
  void signOut().finally(() => {
    window.location.reload();
  });
}
